import React, { forwardRef, useCallback } from 'react';
import { useAuthImage } from '../hooks/useAuthImage';

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'>;

interface AuthImageProps extends ImgProps {
  /** Image path/hash served by the ownership-checked /v1/images/* endpoint. */
  path: string | null | undefined;
  /** Defer loading until near the viewport (lists/grids). */
  lazy?: boolean;
  /** Shown while the blob is loading. Defaults to a neutral skeleton. */
  placeholder?: React.ReactNode;
  /** Shown when the fetch fails. Defaults to the placeholder. */
  fallback?: React.ReactNode;
}

/**
 * Drop-in <img> replacement for authenticated/encrypted images. Fetches the
 * bytes with the Bearer token and renders them from a reference-counted
 * blob: URL (see api/imageBlobCache). Forwards a ref to the underlying <img>
 * for callers that need to measure it (e.g. FaceHeatmap).
 */
export const AuthImage = forwardRef<HTMLImageElement, AuthImageProps>(function AuthImage(
  { path, lazy = false, placeholder, fallback, className, style, onLoad, alt = '', ...imgProps },
  forwardedRef,
) {
  const { src, status, ref: gateRef } = useAuthImage(path, { lazy });

  // The gate element needs a ref for the IntersectionObserver. When lazy and
  // not yet loaded, render a wrapper that owns that ref; once we have a blob
  // URL, the <img> itself carries the ref forwarding.
  const setImgRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  if (status === 'error') {
    return (
      <>
        {fallback ?? placeholder ?? (
          <span
            className={className}
            style={{ display: 'inline-block', background: 'var(--c-sand, #eee)', ...style }}
            aria-label={alt || 'Image unavailable'}
          />
        )}
      </>
    );
  }

  if (!src) {
    // Loading / idle: render a gated placeholder element (carries the lazy ref).
    return (
      <span
        ref={gateRef}
        className={className}
        style={{
          display: 'inline-block',
          background: 'var(--c-sand, #eee)',
          ...style,
        }}
        aria-busy
        aria-label={alt || 'Loading image'}
      >
        {placeholder}
      </span>
    );
  }

  return (
    <img
      {...imgProps}
      ref={setImgRef}
      src={src}
      alt={alt}
      className={className}
      style={style}
      onLoad={onLoad}
    />
  );
});
